import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, ArrowLeft, FileText, Clock, Video, FileEdit } from 'lucide-react';
import { getArticle, updateArticle } from '../services/api';

function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  const [editedContent, setEditedContent] = useState({
    title: '',
    summary: '',
    short_script: '',
    long_script: '',
    short_script_text: '',
    long_script_text: ''
  });

  const loadArticle = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getArticle(id);
      setArticle(data);
      setEditedContent({
        title: data.title,
        summary: data.summary || '',
        short_script: data.short_script || '',
        long_script: data.long_script || '',
        short_script_text: data.short_script_text || '',
        long_script_text: data.long_script_text || ''
      });
    } catch (err) {
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updates = {
        title: editedContent.title,
        summary: editedContent.summary,
        short_script: editedContent.short_script,
        long_script: editedContent.long_script,
        short_script_text: editedContent.short_script_text,
        long_script_text: editedContent.long_script_text
      };
      
      const updatedArticle = await updateArticle(id, updates);
      setArticle(updatedArticle);
      
      // Show success message
      setSaving(false);
      // Could add a toast notification here
    } catch (err) {
      setError('Failed to save changes');
      setSaving(false);
    }
  };

  const handleContentChange = (value) => {
    setEditedContent(prev => ({
      ...prev,
      [activeTab]: value
    }));
  };

  const tabs = [
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'short_script', label: 'Short Script (60-90s)', icon: Clock },
    { id: 'long_script', label: 'Long Script (2-4min)', icon: Video },
    { id: 'short_script_text', label: 'Short Script (Text Only)', icon: Clock },
    { id: 'long_script_text', label: 'Long Script (Text Only)', icon: Video }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800">{error || 'Article not found'}</p>
        <button 
          onClick={() => navigate('/articles')}
          className="mt-4 text-red-600 hover:text-red-800 underline"
        >
          Back to Articles
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/articles')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Articles
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {editedContent.title}
            </h1>
            <p className="text-gray-600">
              Created {formatDate(article.created_at)} • Last updated {formatDate(article.updated_at)}
            </p>
          </div>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="p-6">
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Article Title
            </label>
            <input
              type="text"
              id="title"
              value={editedContent.title}
              onChange={(e) => setEditedContent(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tabs.find(tab => tab.id === activeTab)?.label}
            </label>
            <div className="border border-gray-300 rounded-md">
              <ReactQuill
                value={editedContent[activeTab]}
                onChange={handleContentChange}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'align': [] }],
                    ['link'],
                    ['clean']
                  ]
                }}
                theme="snow"
                style={{ height: '400px' }}
              />
            </div>
          </div>

          {/* Content Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <FileEdit className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-900">Content Guidelines</span>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              {activeTab === 'summary' && (
                <>
                  <p>• Keep the summary concise and informative</p>
                  <p>• Highlight the main points and key insights</p>
                  <p>• Use clear, professional language</p>
                </>
              )}
              {activeTab === 'short_script' && (
                <>
                  <p>• Write for 60-90 seconds of video content</p>
                  <p>• Include timing cues and visual suggestions</p>
                  <p>• Focus on project strategy, numbers, and results</p>
                  <p>• Make it engaging for property investors</p>
                </>
              )}
              {activeTab === 'long_script' && (
                <>
                  <p>• Write for 2-4 minutes of video content</p>
                  <p>• Include detailed timing cues and segment breaks</p>
                  <p>• Focus on project strategy, numbers, and results</p>
                  <p>• Provide comprehensive coverage of the project</p>
                </>
              )}
              {activeTab === 'short_script_text' && (
                <>
                  <p>• Pure script text without timestamps or directions</p>
                  <p>• Focus on project strategy, numbers, and results</p>
                  <p>• Suitable for voice-over or reading directly</p>
                  <p>• Clean, professional script content</p>
                </>
              )}
              {activeTab === 'long_script_text' && (
                <>
                  <p>• Pure script text without timestamps or directions</p>
                  <p>• Focus on project strategy, numbers, and results</p>
                  <p>• Suitable for voice-over or reading directly</p>
                  <p>• Comprehensive project coverage</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Original Text Preview (Collapsible) */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border">
        <details className="group">
          <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <span className="font-medium text-gray-900">Original Article Text</span>
            </div>
            <div className="text-gray-500 group-open:rotate-180 transform transition-transform">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </summary>
          <div className="px-6 pb-6">
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                {article.original_text}
              </pre>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}

export default ArticleEditor;
